import styles from './product-item.module.css';
import deepEqual from 'deep-equal';
import classNames from 'classnames';
import SkeletonLoader from '../../../../../components/skeleton-loader/skeleton-loader';
import { memo } from 'react';
import { OrderProduct } from '../../../../../clients/quipli-service/order/order';
import { OrderInputItem } from '../../../../../clients/quipli-service/order/order-calculation';
import {
  ProductLineItem,
  CustomProductLineItem,
  ProductFeeLineItem,
} from '../../../../../clients/quipli-service/financials/financials';
import { Location } from '../../../../../clients/quipli-service/location/location';
import { RentalProductPrice } from '../../../../../clients/quipli-service/pricing/pricing';
import { Order } from '../../../../../clients/quipli-service/order/order';
import { Typography } from '../../../../../components/typography/typography';
import { formatShippingType } from '../../../../../clients/quipli-service/delivery/delivery';
import { ProductItemErrorBoundary } from './product-item-error-boundary';
import { ProductItemTotal } from './product-item-total';
import { ProductItemProperty } from './product-item-property';
import { ProductItemQuantity } from './product-item-quantity';
import { ProductItemDiscount } from './product-item-discount';
import { ProductItemUnitPrice } from './product-item-unit-price';
import { ProductItemUnits } from './product-item-units';
import { ProductItemInfo, ProductItemName, ProductItemOptions } from './product-item-info';
import { ProductItemFees } from './product-item-fees';
import { ProductItemRates } from './product-item-rates';
import { ProductItemDates } from './product-item-dates';
import { ProductItemDelete } from './product-item-delete';
import { ProductItemOwner } from './product-item-owner';
import { ProductItemExtra } from './product-item-extra';
import { ProductItemStatusBadge } from './product-item-status-badge';
import { useProductItemData } from '../../hooks/use-product-item-data';
import { useProductItemFees } from '../../hooks/use-product-item-fees';
import { useProductItemRentalRates } from '../../hooks/use-product-item-rental-rates';
import { useProductItemUnits } from '../../hooks/use-product-item-units';
import { useProductItemDates } from '../../hooks/use-product-item-dates';
import { useProductItemAvailability } from '../../hooks/use-product-item-availability';

interface ProductItemProps {
  order: Order;
  product?: OrderProduct;
  location: Location;
  setEditsValid: (valid: boolean) => void;
  isEditing: boolean;
  isShowingPrices: boolean;
  isCustomOrderRentalRatesEnabled: boolean;
  isTaxInclusive: boolean;
  onlyShowStoreHoursOnDateTimePicker: boolean;
  rentalPrices?: RentalProductPrice[];
  inputItem?: OrderInputItem;
  financialItem?: ProductLineItem | CustomProductLineItem;
  financialFees: ProductFeeLineItem[];
  onChange: (orderInputItem: OrderInputItem) => void;
  onDelete: (orderInputId: string) => void;
  onChangeFee: (inputFee: ProductFeeLineItem, newAmount: number) => void;
  onResetFee: (inputFee: ProductFeeLineItem) => void;
  refresh: () => Promise<void>;
  saleSerialTrackingEnabled: boolean;
  deliveryDescription?: { productId: number; text: string };
  taxCodeId?: string;
}

export const ProductItem = memo<ProductItemProps>(
  ({
    order,
    product,
    location,
    setEditsValid,
    isEditing,
    isShowingPrices,
    isCustomOrderRentalRatesEnabled,
    isTaxInclusive,
    onlyShowStoreHoursOnDateTimePicker,
    rentalPrices,
    inputItem,
    financialItem,
    financialFees,
    onChange,
    onChangeFee,
    onResetFee,
    onDelete,
    refresh,
    saleSerialTrackingEnabled,
    deliveryDescription,
    taxCodeId,
  }) => {
    // Core data hook - only provides unified state
    const { updateInputItem, setTaxedUnitPrice, setTotalTaxPercentage, ...itemState } = useProductItemData({
      isEditing,
      isTaxInclusive,
      isOrderCycleBilled: order.isCycleBilled,
      orderProduct: product,
      inputItem,
      financialItem,
      financialFees,
      rentalPrices,
      deliveryDescription,
      // Tax calculation parameters
      taxCodeId,
      location,
      onChange,
    });

    const availabilityHandlers = useProductItemAvailability({
      isRental: itemState.isRental,
      isNewProduct: itemState.isNewProduct,
      isCustomProduct: itemState.isCustomProduct,
      productId: itemState.productId,
      quantity: itemState.quantity,
      initialStock: itemState.initialStock,
      trackingType: itemState.trackingType,
      startDate: itemState.startDate,
      endDate: itemState.endDate,
      locationId: location.id,
      orderId: order.id,
      name: itemState.name,
      fetchedProduct: itemState.fetchedProduct,
    });

    const feeHandlers = useProductItemFees({
      onChangeFee,
      onResetFee,
    });

    const rentalHandlers = useProductItemRentalRates({
      inputItem,
      customRentalRates: itemState.customRentalRates,
      orderId: order.id,
      location,
      taxCodeId,
      cycleLength: order.cycleBillingTerms?.cycleLength,
      isTaxInclusive,
      isOrderCycleBilled: order.isCycleBilled,
      onChange,
      setTaxedUnitPrice,
      setTotalTaxPercentage,
    });

    const unitHandlers = useProductItemUnits({
      isNewProduct: itemState.isNewProduct,
      id: itemState.id,
      orderId: order.id,
      updateInputItem,
      refresh,
    });

    const dateHandlers = useProductItemDates({
      productId: itemState.productId,
      id: itemState.isNewProduct ? undefined : parseInt(itemState.id),
      startDate: itemState.startDate,
      endDate: itemState.endDate,
      isNewProduct: itemState.isNewProduct,
      quantity: itemState.quantity,
      customRentalRates: itemState.customRentalRates.current,
      orderId: order.id,
      taxCodeId,
      location,
      isTaxInclusive,
      isOrderCycleBilled: order.isCycleBilled,
      cycleBillingTerms: order.cycleBillingTerms,
      updateInputItem,
      setEditsValid,
      checkAvailableQuantity: availabilityHandlers.checkAvailableQuantity,
    });

    if (itemState.isLoading) {
      return <SkeletonLoader style={{ height: '117px', width: '100%', margin: '8px 0' }} />;
    }

    return (
      <ProductItemErrorBoundary>
        <div className="flex-[0_1_100%] flex">
          <ProductItemProperty
            header="Product Name"
            containerClassName={styles.infoContainer}
            contentClassName="min-w-[250px]"
          >
            <ProductItemInfo {...itemState}>
              <ProductItemName
                name={itemState.name}
                isNewProduct={itemState.isNewProduct}
                financialItem={financialItem}
              />
              <ProductItemOptions options={itemState.options} productId={itemState.productId} />
              <ProductItemOptions options={itemState.services} productId={itemState.productId} />
              <ProductItemFees {...itemState} {...feeHandlers} />
              <ProductItemOwner equipmentOwner={itemState.equipmentOwner} />
              <ProductItemStatusBadge {...itemState} orderDocumentType={order.documentType} />
            </ProductItemInfo>
          </ProductItemProperty>

          <ProductItemProperty
            header="Units"
            containerClassName={styles.unitContainer}
            contentClassName="flex-col !items-start justify-center min-w-[150px]"
          >
            <ProductItemUnits
              {...itemState}
              updateUnits={unitHandlers.updateUnits}
              orderDocumentType={order.documentType}
              orderId={order.id}
              qtyAvailable={availabilityHandlers.qtyAvailable}
              showQtyWarning={availabilityHandlers.showQtyWarning}
              saleSerialTrackingEnabled={saleSerialTrackingEnabled}
              locationId={location.id}
              orderStatus={order.status}
            />
          </ProductItemProperty>

          <ProductItemProperty
            header="Rental start / end"
            containerClassName={styles.datesContainer}
            contentClassName="flex-col justify-center"
          >
            <ProductItemDates
              {...itemState}
              {...dateHandlers}
              isOrderCycleBilled={order.isCycleBilled}
              location={location}
              showOverbookWarning={availabilityHandlers.showOverbookWarning}
              onlyShowStoreHoursOnDateTimePicker={onlyShowStoreHoursOnDateTimePicker}
            />
          </ProductItemProperty>

          <ProductItemProperty header="Delivery" containerClassName={styles.deliveryContainer}>
            <Typography type="text-sm" weight="regular" className={classNames(styles.fieldContentText)}>
              {formatShippingType(itemState.delivery.type, itemState.type)}
            </Typography>
          </ProductItemProperty>

          <ProductItemProperty header="Unit Price" containerClassName={styles.priceContainer} selfEnd>
            <ProductItemUnitPrice {...itemState} updateInputItem={updateInputItem} currency={location.currency} />
          </ProductItemProperty>

          <ProductItemProperty header="Dis. %" containerClassName={styles.discountContainer} selfEnd>
            <ProductItemDiscount {...itemState} updateInputItem={updateInputItem} />
          </ProductItemProperty>

          <ProductItemProperty header="Qty." containerClassName={styles.quantityContainer} selfEnd>
            <ProductItemQuantity {...itemState} updateInputItem={updateInputItem} />
          </ProductItemProperty>

          <ProductItemProperty
            header="Total"
            action={<ProductItemDelete id={itemState.id} isEditing={isEditing} onDelete={onDelete} />}
            containerClassName={styles.totalContainer}
            headerClassName="flex"
            selfEnd
          >
            <ProductItemTotal {...itemState} currency={location.currency} />
          </ProductItemProperty>
        </div>

        <ProductItemRates
          {...itemState}
          {...rentalHandlers}
          isShowingPrices={isShowingPrices}
          isCustomOrderRentalRatesEnabled={isCustomOrderRentalRatesEnabled}
          currency={location.currency}
        />

        <ProductItemExtra isEditing={itemState.isEditing} delivery={itemState.delivery} />
      </ProductItemErrorBoundary>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.order.id === nextProps.order.id &&
      prevProps.isEditing === nextProps.isEditing &&
      prevProps.isShowingPrices === nextProps.isShowingPrices &&
      deepEqual(prevProps.product, nextProps.product) &&
      deepEqual(prevProps.inputItem, nextProps.inputItem) &&
      deepEqual(prevProps.financialItem, nextProps.financialItem) &&
      deepEqual(prevProps.financialFees, nextProps.financialFees)
    );
  },
);

ProductItem.displayName = 'ProductItem';
